import { humanize } from '../../shared/labels';
import { safeFormatAge, safeFormatRelative, safeGetTimestampAge } from '../../shared/utils/dateUtils';
import {
  BaselineScope,
  BaselineStackStatus,
  type AccountBaselineView,
  type BaselineScopeType,
  type BaselineStackView,
  type ProbeView,
} from '../../types/backend';

/**
 * A gate's state.
 *
 * `pending` is observed and not yet done. `unobserved` is different in kind:
 * nothing in OpsPilot performs the check, so it carries no state marker rather
 * than a fourth colour, which would say the check exists and is waiting.
 */
export type GateState =
  | 'done'
  | 'running'
  | 'failed'
  | 'pending'
  | 'unwanted'
  /** We tried to check and could not. Not the same as checked-and-no. */
  | 'inconclusive'
  | 'unobserved';

/** Who moves this gate. The reader's first question about anything stuck. */
export type GateOwner = 'Cloud2' | 'Customer' | 'OpsPilot';

/**
 * One region a deploy has to land in, with its own state. Every region
 * deploys.
 *
 * `state` and `wanted` are separate because they answer different questions.
 * Convergence is where the stack got to; wanted is whether anything still
 * asks for it. Folding them together would lose the convergence state at the
 * moment it matters most, since how settled a stack is says how hard removing
 * it will be.
 */
export interface RegionTarget {
  region: string;
  isMain: boolean;
  state: GateState;
  wanted: boolean;
}

export interface OnboardingGate {
  key: string;
  title: string;
  /** What it means, or what is needed. Shown when the gate is not done. */
  detail: string;
  owner?: GateOwner;
  state: GateState;
  targets: RegionTarget[];
  /** Why it failed, verbatim from the backend. */
  reason?: string;
  /**
   * When the fact in `detail` was observed. A gate that carries one shows its
   * detail even when done, since how recent it is is the point, with the
   * exact UTC time on hover.
   */
  at?: string;
}

function regionState(stack?: BaselineStackView): GateState {
  if (!stack) return 'pending';
  switch (stack.status) {
    case BaselineStackStatus.CONVERGED: return 'done';
    case BaselineStackStatus.REFUSED:
    case BaselineStackStatus.BLOCKED: return 'failed';
    case BaselineStackStatus.NOT_STARTED: return 'pending';
    default: return 'running';
  }
}

/** The same ordering for one stack: sentence, then list, then code. */
export function stackReason(stack: BaselineStackView): string | undefined {
  return stack.summary || stack.refusals?.join('; ') || stack.blocked_reason || undefined;
}

/**
 * What an unwanted stack needs, which depends on how settled it is: a
 * converged stack is a clean delete, one mid-flight or blocked is not.
 */
function unwantedDetail(stacks: BaselineStackView[]): string {
  const unwanted = stacks.filter((s) => !s.desired);
  const settled = unwanted.every((s) => s.status === BaselineStackStatus.CONVERGED);
  return settled
    ? 'Held but no longer wanted. Nothing will remove it; a person has to'
    : 'Held but no longer wanted, and not settled. Check its state before removing it';
}

/** A deploy is as far along as its least-far region. */
function rollUp(targets: RegionTarget[]): GateState {
  if (targets.length === 0) return 'unobserved';
  if (targets.some((t) => t.state === 'failed')) return 'failed';
  // Ahead of running and pending: an unwanted stack needs a person, and
  // nothing will move it on its own. `discover` emits no work for an account
  // with no marked component, so it is not mid-anything (plan A30).
  if (targets.some((t) => !t.wanted)) return 'unwanted';
  if (targets.every((t) => t.state === 'done')) return 'done';
  if (targets.some((t) => t.state === 'running')) return 'running';
  return 'pending';
}

/**
 * Why a stack stopped, in the words most use to a person.
 *
 * `summary` is the sentence: what happened and what it means for the account.
 * `refusals` is the list of things that collided. `blocked_reason` is a code
 * like RESOURCES_ALREADY_EXIST, which is the machine talking to itself and is
 * only shown when nothing better exists.
 */
function firstReason(stacks: BaselineStackView[]): string | undefined {
  const halted = stacks.find((s) => s.status === BaselineStackStatus.REFUSED || s.status === BaselineStackStatus.BLOCKED);
  if (!halted) return undefined;
  return halted.summary || halted.refusals?.join('; ') || halted.blocked_reason || undefined;
}
/**
 * The gates an account passes on its way to onboarded, in the order they must
 * hold, each with the state it is actually in.
 *
 * Deploy gates carry one target per region they have to land in, because a
 * region is a deployment rather than a label: an account converged in one of
 * three regions is not two thirds of a green tick, it is one region done and
 * two waiting, and only per-region state says which.
 */
/**
 * What a stack's `scope` says about where it must land.
 *
 * Two cases, not three: the reconciler owns the baselines and states what
 * each stack is, so a stack is either deployed once for the account or once
 * per region. The catalog's third case, a pinned single region, went with the
 * catalog.
 *
 * The backend carries this rather than the frontend guessing: a stack seen in
 * one region could be an account-scoped one or a per-region one with two
 * regions still untouched, and those are different accounts to look at.
 */
function wantedRegions(
  scope: BaselineScopeType | undefined,
  account: AccountBaselineView,
  seen: string[],
): string[] {
  const supported = account.supported_regions ?? [];
  if (scope === BaselineScope.ACCOUNT) return account.main_region ? [account.main_region] : seen;
  if (scope === BaselineScope.REGION) return [...new Set([...supported, ...seen])];
  // No scope reaches a caller only from a response that predates the field.
  // Show what is there rather than inventing a target set.
  return seen;
}

/**
 * A deploy gate per component the account actually carries, in tier order.
 *
 * Not three hardcoded rows. The components come from the account's own
 * stacks, so a fourth component marked tomorrow appears here without a
 * frontend change, and an account carrying one shows one.
 */
function deployGates(account: AccountBaselineView): OnboardingGate[] {
  const stacks = account.stacks ?? [];

  const byComponent = new Map<string, BaselineStackView[]>();
  for (const stack of stacks) {
    const existing = byComponent.get(stack.component_name);
    if (existing) existing.push(stack);
    else byComponent.set(stack.component_name, [stack]);
  }

  return [...byComponent.entries()]
    .sort((a, b) => Math.min(...a[1].map((s) => s.tier)) - Math.min(...b[1].map((s) => s.tier)))
    .map(([componentName, componentStacks]) => {
      const scope = componentStacks.find((s) => s.scope)?.scope;
      const seen = [...new Set(componentStacks.map((s) => s.region))];

      const targets: RegionTarget[] = wantedRegions(scope, account, seen).map((region) => {
        const stack = componentStacks.find((s) => s.region === region);
        return {
          region,
          isMain: region === account.main_region,
          state: regionState(stack),
          // A region with no row yet is one we want and have not deployed.
          wanted: stack ? stack.desired : true,
        };
      });

      const state = rollUp(targets);

      return {
        key: `deploy:${componentName}`,
        title: componentName,
        detail: state === 'unwanted'
          ? unwantedDetail(componentStacks)
          : scope === BaselineScope.REGION
            ? 'Every supported region'
            : scope === BaselineScope.ACCOUNT
              ? 'Once for the account, in its primary region'
              : 'Where it is deployed today',
        owner: 'OpsPilot' as GateOwner,
        state,
        targets,
        reason: firstReason(componentStacks),
      };
    });
}

/**
 * The credential a stack converged with. Tier 0 installs the access roles and
 * legitimately uses the seed role to do it.
 */
const SEED_CREDENTIAL = 'onboarding';
const ACCESS_ROLES_TIER = 0;

/**
 * Whether this stack converged on the seed role when it should not have.
 *
 * Tier 0 installs the access roles and legitimately uses it; any later tier
 * means the automation role was not assumable and the lifeline carried the
 * convergence. Exported because the stage note and the per-stack marker are
 * two surfaces for one fact, and inlining the rule twice would let them drift
 * apart the first time either constant changes.
 */
export function convergedOnSeedRole(stack: BaselineStackView): boolean {
  return stack.tier > ACCESS_ROLES_TIER && stack.credential_used === SEED_CREDENTIAL;
}

/** The probe that answers whether OpsPilot can reach the account at all. */
export const REACHABILITY_PROBE = 'reachability';

/**
 * A probe is a live assume plus a call, so `checked_at` says when we looked.
 * "Yes" and "yes, four minutes ago" are different claims and only the second
 * can go stale where someone can see it.
 *
 * `inconclusive` is its own state, never a failure: it means a fault on our
 * side stopped us checking, and rendering it as "no" would send someone to
 * phone a customer about an IAM role that is correct.
 */
function probeState(probe: ProbeView | undefined): { state: GateState; reason?: string } {
  if (!probe) return { state: 'unobserved' };
  if (probe.inconclusive) {
    return { state: 'inconclusive', reason: probe.summary || 'the check could not be completed' };
  }
  if (!probe.ok) return { state: 'failed', reason: probe.summary || undefined };
  return { state: 'done' };
}

/** When we last looked, said plainly enough that a stale answer reads as stale. */
function checkedWhen(probe: ProbeView | undefined): string {
  if (!probe?.checked_at) return '';
  return `checked ${safeFormatRelative(probe.checked_at, 'at an unreadable time').toLowerCase()}`;
}

/** The stack that creates the Cloud2 roles the pointers must name. */
const ACCESS_ROLES_COMPONENT = 'cloud2-access-roles';

/**
 * The Cloud2 role each pointer names once onboarding is finished, in the
 * order a reader would want them reported.
 */
const PROPER_ROLES = [
  { pointer: 'read_role_name', label: 'read', role: 'cloud2-operator-readonly' },
  { pointer: 'write_role_name', label: 'write', role: 'cloud2-operator-admin' },
  { pointer: 'automation_role_name', label: 'automation', role: 'cloud2-automation-execution-role' },
] as const;

/**
 * Whether OpsPilot works through the Cloud2 roles rather than the seed role.
 *
 * The reconciler converges the pointers (`converge_role_pointers`), so this is
 * OpsPilot's step. A pointer naming the right role counts only once the stack
 * that creates those roles has converged: before that the name points at a
 * role that may not exist yet.
 */
function properRolesGate(account: AccountBaselineView): OnboardingGate {
  const gate = {
    key: 'proper-roles',
    title: 'Operating on the proper roles',
    owner: 'OpsPilot' as GateOwner,
    targets: [],
  };
  const accessRoles = (account.stacks ?? []).filter(
    (s) => s.component_name === ACCESS_ROLES_COMPONENT && s.desired,
  );
  if (accessRoles.length === 0 || accessRoles.some((s) => s.status !== BaselineStackStatus.CONVERGED)) {
    return { ...gate, state: 'pending', detail: `Waiting for ${ACCESS_ROLES_COMPONENT} to converge` };
  }
  const wrong = PROPER_ROLES.flatMap(({ pointer, label, role }) => {
    const current = account[pointer];
    if (current === role) return [];
    return [current ? `${label} uses ${current}, not ${role}` : `${label} has no role, not ${role}`];
  }).join('; ');
  if (wrong) {
    return { ...gate, state: 'pending', detail: wrong.charAt(0).toUpperCase() + wrong.slice(1) };
  }
  return { ...gate, state: 'done', detail: 'Read, write and automation each use their Cloud2 role' };
}

/**
 * The gates an account passes on its way to onboarded, in the order they must
 * hold, each with the state it is actually in.
 *
 * The human steps are fixed because they are the process; the deploys are
 * whatever the account carries. Deploy gates hold one target per region they
 * land in, because a region is a deployment rather than a label: an account
 * converged in one of three regions is one region done and two waiting, and
 * only per-region state says which.
 */
export function deriveOnboardingGates(account: AccountBaselineView): OnboardingGate[] {
  const deploys = deployGates(account);
  const probes = account.probes ?? [];
  const reachability = probes.find((p) => p.name === REACHABILITY_PROBE);
  const reachabilityState = probeState(reachability);
  // Any probe the view does not know about still gets a rung, so a new check
  // appears without a frontend change rather than being silently dropped.
  const otherProbes = probes.filter((p) => p.name !== REACHABILITY_PROBE);

  return [
    {
      key: 'account',
      title: 'Account registered',
      detail: 'Regions, external id and role are on the account record',
      owner: 'Cloud2',
      state: 'done',
      targets: [],
    },
    {
      key: 'seed',
      title: 'Account reachable',
      detail: checkedWhen(reachability) || 'The customer installs the seed role, on their own timing',
      owner: 'Customer',
      targets: [],
      ...reachabilityState,
      // A recorded block still wins over a probe that has not run.
      ...(account.trust_blocked_reason && reachabilityState.state === 'unobserved'
        ? { state: 'failed' as GateState, reason: account.trust_blocked_reason }
        : {}),
    },
    ...otherProbes.map((probe) => ({
      key: `probe:${probe.name}`,
      title: humanize(probe.name),
      detail: checkedWhen(probe) || probe.summary || 'Checked by OpsPilot',
      owner: 'OpsPilot' as GateOwner,
      targets: [],
      ...probeState(probe),
    })),
    // The reconciler owns the baselines, so every registered account has
    // stages and this row should never render. It stays as the honest reading
    // of a response that carried none, rather than the ladder silently losing
    // its middle.
    ...(deploys.length > 0
      ? deploys
      : [{
          key: 'deploy:none',
          title: 'Baselines deployed',
          detail: 'No baseline stages came back for this account',
          owner: 'OpsPilot' as GateOwner,
          state: 'unobserved' as GateState,
          targets: [],
        }]),
    properRolesGate(account),
    callHomeGate(account.last_call_home_at),
  ];
}

/**
 * How old the last call home may be and still count as calling home: three
 * missed calls of the global baseline's hourly schedule (CallHomeSchedule,
 * rate(1 hour), in cloud2-baseline-global.yaml). Change both together.
 */
const CALL_HOME_MAX_AGE_MS = 3 * 60 * 60 * 1000;

/**
 * Whether the account is calling home now, not whether it ever did. Something
 * records arrival, so an absent timestamp means none has arrived rather than
 * that nobody is listening.
 */
function callHomeGate(lastCallHomeAt: string | null | undefined): OnboardingGate {
  const gate = { key: 'call-home', title: 'Account calling home', targets: [] };
  if (!lastCallHomeAt) return { ...gate, state: 'pending', detail: 'No call-home received yet' };
  const age = safeGetTimestampAge(lastCallHomeAt);
  const when = `Last call home ${safeFormatAge(lastCallHomeAt, 'at an unreadable time')}`;
  if (age !== null && age <= CALL_HOME_MAX_AGE_MS) {
    return { ...gate, state: 'done', detail: when, at: lastCallHomeAt };
  }
  return { ...gate, state: 'pending', detail: `${when}; expected hourly`, at: lastCallHomeAt };
}

/**
 * How far the account got, for the line above the list.
 *
 * The denominator is every gate, including the ones nothing observes. Counting
 * only gates with data made the bar fill as our knowledge shrank: one tracked
 * gate out of one tracked gate is 100%, so an account that had done nothing
 * read as complete, and an account we could check nothing about would have
 * read as perfect.
 *
 * Because an unobserved gate can never be `done`, the bar cannot reach full
 * while anything is unchecked. That is a property of the arithmetic rather
 * than a rule someone has to remember.
 */
export function summariseGates(gates: OnboardingGate[]): {
  done: number;
  total: number;
  uncheckable: number;
  blocked?: OnboardingGate;
} {
  return {
    done: gates.filter((g) => g.state === 'done').length,
    total: gates.length,
    uncheckable: gates.filter((g) => g.state === 'unobserved').length,
    blocked: gates.find((g) => g.state === 'failed'),
  };
}
